import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Truck, Wand2 } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { SearchBar } from "@/components/Header";
import { ProductGrid } from "@/components/ProductCard";
import { ShopCard } from "@/components/ShopCard";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Loading } from "@/components/RequireAuth";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { imgUrl } from "@/lib/api";

const HERO = imgUrl("/api/uploads/demo/d13.jpg");

const Section = ({ title, link, children, testid }) => (
  <section data-testid={testid} className="mx-auto mt-14 max-w-7xl px-4 md:px-6">
    <div className="mb-5 flex items-end justify-between">
      <h2 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">{title}</h2>
      {link && <Link to={link} className="flex items-center gap-1 text-sm font-semibold text-orange-600 hover:underline">Voir tout <ArrowRight className="h-4 w-4" /></Link>}
    </div>
    {children}
  </section>
);

const Hero = () => {
  const { user } = useAuth();
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-[#FFF7ED] to-white">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 md:grid-cols-[1.2fr_1fr] md:px-6 md:py-20">
        <div className="stagger space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-green-800 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">Lancement au Sénégal</span>
          <h1 className="font-display text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            Le marché de l'Afrique, <span className="text-orange-500">dans votre poche.</span>
          </h1>
          <p className="max-w-lg text-base text-gray-600 md:text-lg">Achetez auprès de boutiques locales vérifiées, payez en FCFA et faites-vous livrer dans votre zone.</p>
          <SearchBar className="max-w-xl" />
          <div className="flex flex-wrap gap-3">
            {user === false && <Button asChild size="lg" className="rounded-full" data-testid="hero-register"><Link to="/inscription">Créer un compte</Link></Button>}
            {!user?.shop && user?.role !== "admin" && (
              <Button asChild size="lg" variant="outline" className="rounded-full border-green-800 text-green-800 hover:bg-green-50" data-testid="hero-become-seller"><Link to="/devenir-vendeur">Devenir vendeur</Link></Button>
            )}
          </div>
        </div>
        <div className="relative hidden md:block">
          <img src={HERO} alt="Vendeuse Casadeal" className="aspect-[4/5] w-full rounded-3xl object-cover shadow-xl" />
          <div className="absolute -left-8 bottom-10 flex items-center gap-3 rounded-2xl bg-white/90 p-4 shadow-lg backdrop-blur-xl">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-indigo-500 text-white"><Wand2 className="h-5 w-5" /></span>
            <div><p className="font-display font-bold">Fiche produit par IA</p><p className="text-xs text-gray-500">Objectif : moins de 4 minutes</p></div>
          </div>
        </div>
      </div>
      <div className="kente h-1.5" />
    </section>
  );
};

const Trust = () => (
  <div className="mx-auto mt-8 grid max-w-7xl grid-cols-1 gap-3 px-4 sm:grid-cols-3 md:px-6">
    {[[ShieldCheck, "Vendeurs validés", "Chaque boutique est examinée par Casadeal"], [Truck, "Livraison locale", "Zones, frais et délais fixés par le vendeur"], [Sparkles, "Paiement à la livraison", "Wave et Orange Money : bientôt"]].map(([I, t, d]) => (
      <div key={t} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
        <I className="h-6 w-6 text-green-800" /><div><p className="text-sm font-bold">{t}</p><p className="text-xs text-gray-500">{d}</p></div>
      </div>
    ))}
  </div>
);

export default function Home() {
  const { data } = useApi("/home");
  return (
    <div data-testid="home-page">
      <Hero />
      <Trust />
      {!data ? <Loading /> : (
        <>
          <Section title="Catégories" testid="home-categories">
            <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2">
              {data.categories.map((c) => (
                <Link key={c.id} to={`/recherche?categorie=${c.slug}`} data-testid={`category-${c.slug}`} className="flex min-w-[140px] snap-start flex-col items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-transform duration-200 hover:-translate-y-1 hover:border-orange-300">
                  <span className="grid h-10 w-10 place-items-center rounded-lg bg-orange-100 text-orange-600"><CategoryIcon name={c.icon} /></span>
                  <span className="text-sm font-bold">{c.name}</span>
                </Link>
              ))}
            </div>
          </Section>
          {data.promos.length > 0 && (
            <Section title="Offres promotionnelles" link="/recherche" testid="home-promos"><ProductGrid items={data.promos} testid="promo-grid" /></Section>
          )}
          <Section title="Produits populaires" link="/recherche" testid="home-popular"><ProductGrid items={data.popular} testid="popular-grid" /></Section>
          <Section title="Boutiques à découvrir" link="/boutiques" testid="home-shops">
            <div className="no-scrollbar -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2">{data.shops.map((s) => <ShopCard key={s.id} s={s} />)}</div>
          </Section>
          <SellerBanner />
          <Section title="Nouveautés" link="/recherche?tri=recent" testid="home-latest"><ProductGrid items={data.latest} testid="latest-grid" /></Section>
        </>
      )}
    </div>
  );
}

const SellerBanner = () => (
  <section className="mx-auto mt-16 max-w-7xl px-4 md:px-6">
    <div className="grid overflow-hidden rounded-3xl bg-green-900 text-white md:grid-cols-2">
      <div className="space-y-4 p-8 md:p-12">
        <p className="text-xs font-bold uppercase tracking-widest text-orange-300">Vendeurs</p>
        <h2 className="font-display text-3xl font-black leading-tight md:text-4xl">Ouvrez votre boutique. L'IA rédige vos fiches produits.</h2>
        <p className="text-green-100">Une photo ou un lien suffit : titre, description, catégorie et caractéristiques préparés en brouillon. Vous vérifiez, fixez votre prix et publiez.</p>
        <div className="flex flex-wrap gap-3">
          <Button asChild size="lg" className="rounded-full" data-testid="banner-become-seller"><Link to="/devenir-vendeur">Devenir vendeur</Link></Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10" data-testid="banner-pricing"><Link to="/abonnements">Voir les abonnements</Link></Button>
        </div>
      </div>
      <img src={imgUrl("/api/uploads/demo/d15.jpg")} alt="Vendeur" className="h-64 w-full object-cover md:h-full" />
    </div>
  </section>
);
